import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, Search, Pencil } from "lucide-react";

const STATUS_LABEL = { in_use: "In Use", storage: "Storage", repair: "Repair", retired: "Retired" };

export default function AccountingView() {
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: "", status: "all", category: "all" });
  const [categories, setCategories] = useState([]);

  const load = async () => {
    setLoading(true);
    const params = {};
    if (filters.search) params.search = filters.search;
    if (filters.status !== "all") params.status = filters.status;
    if (filters.category !== "all") params.category = filters.category;
    try {
      const { data } = await api.get("/assets", { params });
      setAssets(data);
    } catch (e) { /* silent */ }
    setLoading(false);
  };

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  const totals = assets.reduce((acc, a) => ({
    cost: acc.cost + (Number(a.purchase_cost) || 0),
    eoy1: acc.eoy1 + (Number(a.eoy1) || 0),
    eoy2: acc.eoy2 + (Number(a.eoy2) || 0),
    eoy3: acc.eoy3 + (Number(a.eoy3) || 0),
    eoy4: acc.eoy4 + (Number(a.eoy4) || 0),
  }), { cost: 0, eoy1: 0, eoy2: 0, eoy3: 0, eoy4: 0 });

  return (
    <div data-testid="accounting-page">
      <PageHeader
        eyebrow="[05] Finance"
        title="Accounting View"
        subtitle="Cost basis, depreciation schedule and end-of-year book values across the inventory. All monetary figures are NZD and GST exclusive."
        actions={
          <a
            href={`${API}/assets/export/csv`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="accounting-export-btn"
            className="inline-flex items-center justify-center rounded-none border border-[#D6CFC1] bg-transparent text-[#3A3A3A] hover:text-[#0F0F0F] hover:bg-[#F0EDE3] font-mono text-xs tracking-wider uppercase h-9 px-4 transition-colors"
          >
            <Download className="w-3.5 h-3.5 mr-2" />Export CSV
          </a>
        }
      />

      <div className="p-8 space-y-6">
        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-[#E8E3D6] border border-[#E5E0D5]">
          <Kpi label="Total Cost Basis" value={`NZ$${totals.cost.toLocaleString()}`} testid="kpi-cost" />
          <Kpi label="Σ EOY1" value={`NZ$${totals.eoy1.toLocaleString()}`} testid="kpi-eoy1" />
          <Kpi label="Σ EOY2" value={`NZ$${totals.eoy2.toLocaleString()}`} testid="kpi-eoy2" />
          <Kpi label="Σ EOY3" value={`NZ$${totals.eoy3.toLocaleString()}`} testid="kpi-eoy3" />
          <Kpi label="Σ EOY4" value={`NZ$${totals.eoy4.toLocaleString()}`} testid="kpi-eoy4" />
        </div>

        {/* Filter bar */}
        <div className="grid grid-cols-1 md:grid-cols-[1fr_180px_180px] gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#7A7A7A]" />
            <Input
              data-testid="accounting-search"
              placeholder="Search by tag, name, serial, assignee…"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="pl-10 rounded-none bg-white border-[#E5E0D5] font-mono text-sm focus-visible:ring-1 focus-visible:ring-[#F37B2A] focus-visible:ring-offset-0"
            />
          </div>
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
            <SelectTrigger data-testid="accounting-filter-status" className="rounded-none bg-white border-[#E5E0D5] font-mono text-xs uppercase tracking-wider"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none bg-white border-[#D6CFC1]">
              <SelectItem value="all">All Statuses</SelectItem>
              {Object.entries(STATUS_LABEL).map(([k,v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.category} onValueChange={(v) => setFilters({ ...filters, category: v })}>
            <SelectTrigger data-testid="accounting-filter-category" className="rounded-none bg-white border-[#E5E0D5] font-mono text-xs uppercase tracking-wider"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none bg-white border-[#D6CFC1]">
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <div className="border border-[#E5E0D5] bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="accounting-table">
            <thead>
              <tr className="border-b border-[#E5E0D5] text-[#7A7A7A]">
                <Th>Tag</Th>
                <Th>Splashtop Name</Th>
                <Th>Category</Th>
                <Th align="right">Purchase Date</Th>
                <Th align="right">Purchase Price (NZD)</Th>
                <Th>Depreciation Type</Th>
                <Th align="right">Rate (%)</Th>
                <Th align="right">EOY1 (NZD)</Th>
                <Th align="right">EOY2 (NZD)</Th>
                <Th align="right">EOY3 (NZD)</Th>
                <Th align="right">EOY4 (NZD)</Th>
                <Th align="right"></Th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={12} className="px-4 py-10 text-center font-mono text-xs text-[#7A7A7A]">Loading…</td></tr>}
              {!loading && assets.length === 0 && <tr><td colSpan={12} className="px-4 py-12 text-center font-mono text-xs text-[#7A7A7A]">No assets in the current filter.</td></tr>}
              {assets.map((a) => (
                <tr key={a.id} data-testid={`accounting-row-${a.id}`} className="border-b border-[#E5E0D5] hover:bg-[#F0EDE3] transition-colors">
                  <Td><Link to={`/assets/${a.id}`} className="font-mono text-xs text-[#0F0F0F] hover:text-[#1A1A1A]">{a.asset_tag || "—"}</Link></Td>
                  <Td><Link to={`/assets/${a.id}`} className="text-[#1A1A1A] whitespace-nowrap hover:text-[#1A1A1A]">{a.name}</Link></Td>
                  <Td><span className="font-mono text-xs text-[#5A5A5A]">{a.category}</span></Td>
                  <Td align="right"><span className="font-mono text-xs text-[#3A3A3A] whitespace-nowrap">{a.purchase_date || "—"}</span></Td>
                  <Td align="right"><span className="font-mono text-xs text-[#3A3A3A] whitespace-nowrap">{a.purchase_cost ? `NZ$${Number(a.purchase_cost).toLocaleString()}` : "—"}</span></Td>
                  <Td><span className="font-mono text-xs text-[#3A3A3A] whitespace-nowrap">{a.depreciation_type || "—"}</span></Td>
                  <Td align="right"><span className="font-mono text-xs text-[#3A3A3A]">{a.depreciation_rate ? `${a.depreciation_rate}%` : "—"}</span></Td>
                  <Td align="right"><span className="font-mono text-xs text-[#3A3A3A] whitespace-nowrap">{a.eoy1 ? `NZ$${Number(a.eoy1).toLocaleString()}` : "—"}</span></Td>
                  <Td align="right"><span className="font-mono text-xs text-[#3A3A3A] whitespace-nowrap">{a.eoy2 ? `NZ$${Number(a.eoy2).toLocaleString()}` : "—"}</span></Td>
                  <Td align="right"><span className="font-mono text-xs text-[#3A3A3A] whitespace-nowrap">{a.eoy3 ? `NZ$${Number(a.eoy3).toLocaleString()}` : "—"}</span></Td>
                  <Td align="right"><span className="font-mono text-xs text-[#3A3A3A] whitespace-nowrap">{a.eoy4 ? `NZ$${Number(a.eoy4).toLocaleString()}` : "—"}</span></Td>
                  <Td align="right">
                    <button
                      onClick={() => navigate(`/assets/${a.id}/edit`)}
                      data-testid={`accounting-edit-${a.id}`}
                      className="p-1.5 text-[#7A7A7A] hover:text-[#0F0F0F]"
                      title="Edit"
                    ><Pencil className="w-3.5 h-3.5" /></button>
                  </Td>
                </tr>
              ))}
            </tbody>
            {!loading && assets.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-[#A8A39A]">
                  <Td><span className="uppercase-label">Totals</span></Td>
                  <Td colSpan={3} />
                  <Td align="right"><span className="font-mono text-xs text-[#0F0F0F] whitespace-nowrap">NZ${totals.cost.toLocaleString()}</span></Td>
                  <Td colSpan={2} />
                  <Td align="right"><span className="font-mono text-xs text-[#0F0F0F] whitespace-nowrap">NZ${totals.eoy1.toLocaleString()}</span></Td>
                  <Td align="right"><span className="font-mono text-xs text-[#0F0F0F] whitespace-nowrap">NZ${totals.eoy2.toLocaleString()}</span></Td>
                  <Td align="right"><span className="font-mono text-xs text-[#0F0F0F] whitespace-nowrap">NZ${totals.eoy3.toLocaleString()}</span></Td>
                  <Td align="right"><span className="font-mono text-xs text-[#0F0F0F] whitespace-nowrap">NZ${totals.eoy4.toLocaleString()}</span></Td>
                  <Td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        <div className="font-mono text-xs text-[#9A9A9A]">Total: {assets.length} {assets.length === 1 ? "asset" : "assets"}</div>
      </div>
    </div>
  );
}

function Kpi({ label, value, testid }) {
  return (
    <div className="bg-white p-5" data-testid={testid}>
      <div className="uppercase-label">{label}</div>
      <div className="font-mono text-xl mt-3 tracking-tight text-[#0F0F0F]">{value}</div>
    </div>
  );
}
function Th({ children, align = "left" }) {
  return <th className={`px-4 py-3 font-mono text-[10px] uppercase tracking-[0.18em] text-[#7A7A7A] text-${align} font-medium whitespace-nowrap`}>{children}</th>;
}
function Td({ children, align = "left", colSpan }) {
  return <td colSpan={colSpan} className={`px-4 py-3 text-${align}`}>{children}</td>;
}
